import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, Grid, IconButton } from '@mui/material';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { ManagementActions } from '../../../../Services/_actions/Management/management.actions.ts';
import SearchBox from '../../../FlowBuilder/Common/SearchBox/index.tsx';
import { Link, useNavigate, useNavigationType } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import Pagination from '../../../FlowBuilder/Common/Pagination/index.tsx';
import OrganisationDropdown from '../../../Shared/OrganisationDropdown/index.tsx';
import { userInformationAtom } from '../../../../Services/_states/auth.js';
import CreateRoleForm from '../../Role/Create.tsx';
import MappinModal from './MappingModal.tsx';
import HomeIcon from '@mui/icons-material/Home';
import ConfirmBox from '../../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { useSnackBar } from '../../../FlowBuilder/Common/SnackBar/index.tsx';
import DynamicBreadcrumbs from '../../../Shared/Breadcrumb/index.tsx';
import useDebounce from '../../../Shared/Delay/index.tsx';
import LoadingDisableWrapper from '../../../Shared/LoadingDisableWrapper/index.tsx';

interface FiltersObj {
  page?: number;
  limit: number;
  search?: string;
  organisation_id?: string | number;
  selectedOrganisation?: any | null;
  sortOrder?: string;
  sortField?: string;
}

interface OrganisationMappingObject {
  index: number;
  count: number;
  name: string;
  organisation: {
    name: string;
  };
}
interface OrganisationResponse {
  list: OrganisationMappingObject[];
  count: number;
  pages: number;
  current_page: number;
  has_next: boolean;
  has_previous: boolean;
}
const OrganisationMapping: React.FC = () => {
  const actions = ManagementActions();
  const loggedInUser = useRecoilValue(userInformationAtom);
  interface OrganisationResponse {
    list: OrganisationMappingObject[];
    count: number;
    pages: number;
    current_page: number;
    has_next: boolean;
    has_previous: boolean;
  }
  const [organisationResponse, setOrganisationResponse] = useState<OrganisationResponse>({
    list: [],
    count: 0,
    pages: 0,
    current_page: 1,
    has_next: false,
    has_previous: false,
  });
  const { showSnackBar } = useSnackBar();
  const [isLoading, setIsLoading] = useState(false);
  const [filtersObj, setFiltersObj] = useState<FiltersObj>({
    limit: 10,
    search: '',
    page: 0,
    sortOrder: 'desc',
    sortField: 'created_at',
    organisation_id: loggedInUser?.profile?.organisation?.organisation_id || '',
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [organasationMap, setorganasationMap] = useState();
  const [searchInput, setSearchInput] = useState(filtersObj?.search || '');
  const debouncedSearch = useDebounce(searchInput, 500);

  const getMappingList = async (filtersObj: FiltersObj) => {
    setIsLoading(true);
    try {
      const res = await actions.getOrganisationMapping(filtersObj);

      if (res?.status === 200) {
        const {
          list = [],
          count = 0,
          pages = 0,
          current_page = 1,
          has_next = false,
          has_previous = false,
        } = res.data || {};
        setOrganisationResponse({ list, count, pages, current_page, has_next, has_previous });
      }
    } catch {
      showSnackBar('Failed to fetch organisation mapping:', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (formData: any) => {
    if (!formData) {
      setIsModalOpen(false);
      return;
    }
    try {
      const dataToSend = {
        first_organisation_id: formData?.firstOrganisation?.organisation_id,
        second_organisation_id: formData?.secondOrganisation?.organisation_id,
      };
      const res = await actions.postOrganisationMap(dataToSend);
      if (res?.status === 200 || res?.status === 201) {
        setorganasationMap(res.data || []);
        getMappingList(filtersObj);
        setIsModalOpen(false);
        showSnackBar('Organisation Mapping created successfully', 'success');
      } else {
        showSnackBar('Failed to create organisation mapping:', 'error');
      }
    } catch (error) {
      showSnackBar('Error creating organisation mapping:', 'error');
    }
  };

  const handleChangePage = (event, newPage) => {
    getMappingList({ ...filtersObj, page: newPage });
    setFiltersObj({ ...filtersObj, page: newPage });
  };
  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value) || 10;
    getMappingList({
      ...filtersObj,
      limit: value,
      page: 0,
    });
    setFiltersObj({
      ...filtersObj,
      limit: value,
      page: 0,
    });
  };
  useEffect(() => {
  setFiltersObj((prev) => ({ ...prev, search: debouncedSearch, page: 0 }));
  getMappingList({ ...filtersObj, search: debouncedSearch, page: 0 });
}, [debouncedSearch]);

  const columns = useMemo<MRT_ColumnDef<OrganisationMappingObject>[]>(
    () => [
      {
        accessorKey: 'index',
        header: 'S.No.',
        enableSorting: false,
        size: 50,
        Cell: ({ row }) => {
          const rowIndex = row.index;
          const page = filtersObj?.page ?? 0; // Fallback to 0 if undefined
          const limit = filtersObj?.limit ?? 10; // Fallback to 10 if undefined
          return page * limit + rowIndex + 1;
        },

        muiTableHeadCellProps: {
          sx: {
            backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
            fontFamily: 'system-ui',
            paddingLeft: '20px !important', // header padding here
          },
        },
        muiTableBodyCellProps: {
          sx: {
            backgroundColor: '#fafafa',
            paddingLeft: '20px !important',
          },
        },
      },
      {
        accessorKey: 'first_organisation.name',
        header: 'Source Organisation Name',
        enableSorting: false,
        size: 200,
      },
      {
        accessorKey: 'first_organisation.category',
        header: 'Organisation Category',
        enableSorting: false,
        size: 200,
      },
      {
        accessorKey: 'second_organisation.name',
        header: 'Organisation Working With',
        enableSorting: false,
        size: 200,
      },
      {
        accessorKey: 'second_organisation.category',
        header: 'Organisation Category',
        enableSorting: false,
        size: 150,
      },
      // {
      //     header: 'Delete',
      //     enableSorting: false,
      //     size: 100,
      //     Cell: ({ row }) => (
      //         <Link
      //             to="#"
      //             onClick={() => {
      //                 handleDelete(row?.original);
      //             }}
      //             style={{ color: 'red', cursor: 'pointer' }}
      //         >
      //             Delete
      //         </Link>
      //     ),
      // }
    ],
    [filtersObj]
  );
  const handleDelete = (row: OrganisationMappingObject) => {
    setConfirmOpen(true);
  };
  const handleConfirmDelete = (confirmed: boolean) => {
    if (confirmed) {
      setConfirmOpen(false);
    }
    setConfirmOpen(false);
  };
  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      onClick: () => (window.location.href = '/'),
    },
    { label: 'Organisations Mapping' },
  ];
  return (
    <Box
      sx={{
        padding: '5px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }}
    >
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item: { label: string; href: string }) => {
          if (item) {
            navigate(item?.href);
          }
        }}
      />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Organisations Mapping
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setIsModalOpen(true);
            }}
          >
            Add
          </Button>
        </Grid>
      </Grid>
      {/* <Grid container sx={{ alignItems: 'center' }} className='search-box-style'>
                <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center', pb: 0 }}>
                    <SearchBox
                        handleSearch={(e) => {
                            setFiltersObj({ ...filtersObj, search: e, page: 0 });
                            getMappingList({ ...filtersObj, search: e, page: 0 });
                        }}
                        value={filtersObj?.search || ''}
                    />
                </Grid>
                {loggedInUser?.is_superuser &&
                    <Grid item xs={12} md={3} textAlign="right" sx={{ pb: 0.7 }}>
                        <OrganisationDropdown
                            value={filtersObj?.selectedOrganisation || null}
                            onChange={(org) => {
                                setFiltersObj({ ...filtersObj, selectedOrganisation: org || null, organisation_id: org?.organisation_id || '' });
                                getMappingList({ ...filtersObj, selectedOrganisation: org || null, organisation_id: org?.organisation_id || '' })
                            }}
                            label={"Select Organisation"}
                        />
                    </Grid>
                }
            </Grid> */}
      <Grid
        container
        spacing={{ xs: 0, sm: 2 }}
        sx={{ alignItems: 'center', mb: { xs: 1, sm: 2 } }}
        className="search-box-style"
      >
        <Grid item xs={12} sm={loggedInUser?.is_superuser ? 9 : 12}>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: 2, sm: 0 } }}>
            <LoadingDisableWrapper loading = {isLoading}>
            <SearchBox handleSearch={(query) => setSearchInput(query)} value={searchInput} disabled = {isLoading}/>
              </LoadingDisableWrapper>
          </Box>
        </Grid>
        {loggedInUser?.is_superuser && (
          <Grid item xs={12} sm={3}>
            <OrganisationDropdown
              value={filtersObj?.selectedOrganisation || null}
              onChange={(org) => {
                setFiltersObj({
                  ...filtersObj,
                  selectedOrganisation: org || null,
                  organisation_id: org?.organisation_id || '',
                });
                getMappingList({
                  ...filtersObj,
                  selectedOrganisation: org || null,
                  organisation_id: org?.organisation_id || '',
                });
              }}
              label={'Select Organisation'}
            />
          </Grid>
        )}
      </Grid>

      <MaterialReactTable
        columns={columns}
        data={organisationResponse.list || []}
        state={{
          isLoading,
        }}
        muiTableBodyCellProps={{
          sx: (theme) => ({
            backgroundColor:
              theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
          }),
        }}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 390px)', width: '100%' } }}
        muiTableHeadCellProps={{
          sx: {
            backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
            fontFamily: 'system-ui',
          },
        }}
        enableTopToolbar={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableHiding={false}
        enableStickyHeader={true}
        initialState={{
          density: 'comfortable',
        }}
        enablePagination={false}
        manualPagination={false}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
            <Pagination
              totalCount={organisationResponse?.count || 0}
              rowsPerPage={filtersObj?.limit}
              page={filtersObj?.page}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
      {isModalOpen && <MappinModal open={isModalOpen} onClose={handleSubmit} />}
      <ConfirmBox
        open={confirmOpen}
        onClose={handleConfirmDelete}
        options={{
          title: 'Delete Role',
          text: 'Are you sure you want to delete this Organisation Map?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />
    </Box>
  );
};

export default OrganisationMapping;
